import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { qk } from "./queryKeys";
import {
  listCases,
  getCase,
  createCase,
  updateCase,
  deleteCase,
  addInstanceToCase,
  removeInstanceFromCase,
} from "@/api/cases";
import { toast } from "sonner";
import { toastFromError } from "@/lib/errors";
import type { CaseStatus } from "@/api/types";

export function useCases(params: {
  status?: CaseStatus;
  q?: string;
  page?: number;
  pageSize?: number;
} = {}) {
  return useQuery({
    queryKey: qk.cases(params),
    queryFn: () => listCases(params),
    staleTime: 15_000,
  });
}

export function useCase(id: string | undefined) {
  return useQuery({
    queryKey: qk.case(id ?? ""),
    queryFn: () => getCase(id!),
    enabled: !!id,
    staleTime: 30_000,
  });
}

export function useCreateCase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof createCase>[0]) => createCase(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cases"], exact: false });
      toast.success("Case created");
    },
    onError: (err) => toastFromError(err),
  });
}

export function useUpdateCase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: Parameters<typeof updateCase>[1];
    }) => updateCase(id, body),
    onSuccess: (data) => {
      qc.setQueryData(qk.case(data.id), data);
      qc.invalidateQueries({ queryKey: ["cases"], exact: false });
    },
    onError: (err) => toastFromError(err),
  });
}

export function useDeleteCase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, hard }: { id: string; hard?: boolean }) =>
      deleteCase(id, hard),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cases"], exact: false });
      toast.success("Case deleted");
    },
    onError: (err) => toastFromError(err),
  });
}

export function useAddInstanceToCase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      caseId,
      instanceId,
    }: {
      caseId: string;
      instanceId: string;
    }) => addInstanceToCase(caseId, instanceId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cases"], exact: false });
      qc.invalidateQueries({ queryKey: ["instances"], exact: false });
      toast.success("Entity added to case");
    },
    onError: (err) => toastFromError(err),
  });
}

export function useRemoveInstanceFromCase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      caseId,
      instanceId,
    }: {
      caseId: string;
      instanceId: string;
    }) => removeInstanceFromCase(caseId, instanceId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cases"], exact: false });
      qc.invalidateQueries({ queryKey: ["instances"], exact: false });
      toast.success("Entity removed from case");
    },
    onError: (err) => toastFromError(err),
  });
}
